import React, { useState } from 'react';
import styles from './AdminLogin.module.css';
import { logger } from '../utils/logger';

interface AdminLoginProps {
  onLogin: (authToken: string, user: any) => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLogin }) => {
  const [authToken, setAuthToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!authToken.trim()) {
      setError('Please enter your auth token');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Verify token by fetching current user
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/users/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Invalid auth token');
      }

      const user = await response.json();
      
      // Check if user is admin
      if (user.telegramId !== '205613459') {
        throw new Error('Access denied: Admin privileges required');
      }

      // Store token and user
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('currentUser', JSON.stringify(user));

      logger.info('✅ Admin authenticated successfully');
      onLogin(authToken, user);
    } catch (error: unknown) {
      logger.error('Admin login failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1 className={styles.title}>🛡️ Admin Access</h1>
        <p className={styles.subtitle}>Enter your auth token to continue</p>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputGroup}>
            <label htmlFor="authToken">Auth Token</label>
            <textarea
              id="authToken"
              className={styles.textarea}
              value={authToken}
              onChange={(e) => setAuthToken(e.target.value)}
              placeholder="Paste your auth token here..."
              rows={4}
              disabled={loading}
            />
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <button 
            type="submit" 
            className={styles.submitBtn}
            disabled={loading || !authToken.trim()}
          >
            {loading ? '⏳ Verifying...' : '🔓 Access Admin Dashboard'}
          </button>
        </form>

        <div className={styles.help}>
          <h3>How to get your auth token:</h3>
          <ol>
            <li>Open the Settler app in Telegram</li>
            <li>Go to <strong>Settings</strong></li>
            <li>Scroll down to <strong>"Developer Info"</strong></li>
            <li>Tap <strong>"Copy Auth Token"</strong></li>
            <li>Paste it above and click "Access Admin Dashboard"</li>
          </ol>
          
          <div className={styles.note}>
            <strong>Note:</strong> Only authorized admins (Telegram ID: 205613459) can access this dashboard.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;

